import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Creator: React.FC = () => {
  const heroContent = {
    title: 'Your Platform for Impact & Income',
    subtitle: 'Transform your knowledge into a thriving, scalable business with custom pages.',
    description: 'Gyaanly empowers you to build passive income from courses, offer exclusive 1:1 sessions, create custom pages, and engage a global community. Top creators earn $15,000+ monthly.',
  };

  const benefits = [
    { icon: '💰', title: 'Passive Income', description: 'Create once, earn forever. Build multiple income streams through course sales, memberships, and affiliate programs.' },
    { icon: '🌍', title: 'Global Reach', description: 'Teach learners worldwide. Our platform reaches millions of eager students looking for your expertise.' },
    { icon: '⚡', title: 'Scalable Business', description: 'Scale your teaching business without limits. Automate your workflow and focus on what you love.' },
    { icon: '📄', title: 'Custom Branded Pages', description: 'Build and host your own branded pages with our simple drag-and-drop page builder. Create landing pages, portfolios, and more.' },
  ];

  const workflowSteps = [
    { step: '01', title: 'Plan Your Content', description: 'Utilize our intuitive planning tools to structure courses, workshops, and 1:1 sessions for maximum impact.', icon: '📋' },
    { step: '02', title: 'Create & Engage', description: 'Record high-quality video content or host live, interactive 1:1 sessions directly through our platform.', icon: '🎥' },
    { step: '03', title: 'Build Your Pages', description: 'Create custom landing pages, portfolios, and sales pages using our drag-and-drop page builder.', icon: '📄' },
    { step: '04', title: 'Launch & Market', description: 'Reach your ideal audience with built-in marketing tools, promoting both courses and personalized services.', icon: '🚀' },
    { step: '05', title: 'Automate & Grow', description: 'Automate registrations, payments, and scheduling for 1:1 sessions, allowing you to focus on growth and content.', icon: '📈' }
  ];

  const directEngagementFeatures = [
    { title: '1:1 Live Consultations', description: 'Offer personalized advice, coaching, and Q&A sessions directly to your most dedicated learners via live video calls.', icon: '📞' },
    { title: 'Automated Scheduling', description: 'Simplify booking with integrated calendar and automated reminders, allowing learners to easily find and reserve available slots.', icon: '🗓️' },
    { title: 'Secure Communication', description: 'Communicate securely with your learners through built-in chat, file sharing, and call features, ensuring privacy and convenience.', icon: '🔒' },
    { title: 'Flexible Pricing & Packages', description: 'Set your own rates for sessions, offer packages, and manage payments effortlessly through our secure system.', icon: '💸' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 overflow-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-7xl font-bold text-white mb-4 leading-tight sm:text-5xl">{heroContent.title}</h1>
            <p className="text-lg lg:text-2xl text-purple-200 mb-6 max-w-4xl mx-auto sm:text-xl">{heroContent.subtitle}</p>
            <p className="text-base text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed sm:text-lg">{heroContent.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 sm:text-4xl">Why Choose Gyaanly?</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto sm:text-xl">Join thousands of successful creators who are building thriving businesses and making a difference in learners' lives.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="glass-card p-8 text-center"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-white mb-4">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section id="how-it-works" className="py-20 bg-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 sm:text-4xl">Your Path to Success</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto sm:text-xl">Our proven 5-step workflow has helped thousands of creators build successful online businesses.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="relative"
              >
                <div className="glass-card p-8 text-center h-full">
                  <div className="text-4xl mb-4">{step.icon}</div>
                  <div className="text-sm font-bold text-purple-300 mb-2">{step.step}</div>
                  <h3 className="text-xl font-bold text-white mb-4">{step.title}</h3>
                  <p className="text-gray-300">{step.description}</p>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <div className="w-8 h-0.5 bg-purple-400"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Direct Engagement Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 sm:text-4xl">Direct Engagement Features</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto sm:text-xl">Enhance your relationship with learners through direct engagement features.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {directEngagementFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="glass-card p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{feature.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-300 mb-4">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Journey Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 sm:text-4xl">Your Creator Journey</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto sm:text-xl">From beginner to successful creator - we guide you every step of the way.</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
              className="glass-card p-8 text-center"
            >
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-bold text-white mb-4">Getting Started</h3>
              <p className="text-gray-300">Set up your creator profile, choose your niche, and start building your audience with our proven strategies.</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
              className="glass-card p-8 text-center"
            >
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-bold text-white mb-4">Growth & Scaling</h3>
              <p className="text-gray-300">Expand your reach, create more courses, and build multiple income streams as your business grows.</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.2 }}
              className="glass-card p-8 text-center"
            >
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-white mb-4">Success & Mastery</h3>
              <p className="text-gray-300">Become a recognized expert in your field and help thousands of learners achieve their goals.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Page Building & Hosting Section */}
      <section className="py-20 bg-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 sm:text-4xl">Build & Host Your Own Pages</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto sm:text-xl">Create stunning, professional pages in minutes with our intuitive page builder. No coding required.</p>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Create Pages in Minutes</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Drag & Drop Builder</h4>
                    <p className="text-gray-300">Intuitive interface that makes page creation as simple as moving elements around.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Pre-built Templates</h4>
                    <p className="text-gray-300">Choose from dozens of professional templates designed for creators and educators.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Custom Branding</h4>
                    <p className="text-gray-300">Add your logo, colors, and personal touch to make every page uniquely yours.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">One-Click Hosting</h4>
                    <p className="text-gray-300">Deploy your pages instantly with our integrated hosting solution. No technical knowledge required.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, amount: 0.2 }}
              className="relative"
            >
              <div className="glass-card p-8 border border-white/20">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-white/20 rounded w-3/4"></div>
                    <div className="h-4 bg-white/20 rounded w-1/2"></div>
                    <div className="h-4 bg-white/20 rounded w-5/6"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <div className="h-20 bg-purple-500/20 rounded-lg border-2 border-dashed border-purple-400 flex items-center justify-center">
                      <span className="text-purple-200 text-sm">Image</span>
                    </div>
                    <div className="h-20 bg-blue-500/20 rounded-lg border-2 border-dashed border-blue-400 flex items-center justify-center">
                      <span className="text-blue-200 text-sm">Text</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg border border-white/20">
                    <p className="text-white/80 text-sm text-center">Your Custom Page</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>


        </div>
      </section>
    </div>
  );
};

    export default Creator; 